import { useCallback, useEffect, useState } from 'react';
import { Clock, CreditCard, MapPin, Users } from 'lucide-react';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { cn } from '@/lib/utils';
import { clubService, type ClubDashboardOverview } from '@/services/club';
import { hasAcademyManagementRole, hasClubManagementRole } from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';

export interface ClubDashboardSectionProps {
  /** Denser layout when shown beside academy overview */
  compact?: boolean;
  className?: string;
}

function StatSkeleton({ compact }: { compact: boolean }) {
  return (
    <span
      className={cn(
        'inline-block animate-pulse rounded bg-gray-200 dark:bg-gray-700',
        compact ? 'h-4 w-8 sm:h-5 sm:w-10' : 'h-5 w-12 sm:h-7 sm:w-16'
      )}
    />
  );
}

export function ClubDashboardSection({ compact = false, className }: ClubDashboardSectionProps) {
  const { user } = useAuthStore();
  const { hasClubManagement, isLoading: entitlementsLoading } = useClubModuleAccess();
  const { currentClub, isLoading: clubLoading } = useCurrentClub();
  const [overview, setOverview] = useState<ClubDashboardOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const showSection = hasClubManagementRole(user) || hasAcademyManagementRole(user);

  const loadOverview = useCallback(async () => {
    if (!currentClub || !hasClubManagement) return;
    setOverviewLoading(true);
    try {
      const res = await clubService.getDashboardOverview(currentClub.id);
      if (res.success && res.data) {
        setOverview(res.data);
      }
    } finally {
      setOverviewLoading(false);
    }
  }, [currentClub, hasClubManagement]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  if (!showSection) {
    return null;
  }

  if (entitlementsLoading || clubLoading) {
    return (
      <div
        className={cn(
          'flex min-h-[84px] items-center justify-center',
          !compact && 'mb-8',
          className
        )}
        aria-busy="true"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (!hasClubManagement) {
    return null;
  }

  const statsLoading = overviewLoading && !overview;
  const statSize = compact ? 'compact' : 'default';

  return (
    <section
      id={compact ? undefined : TOUR_STEP_IDS.CLUB_BUSINESS_OVERVIEW}
      className={cn(!compact && 'mb-8', className)}
      aria-labelledby="club-dashboard-heading"
    >
      <h2
        id="club-dashboard-heading"
        className={cn(
          'font-semibold text-gray-900 dark:text-white',
          compact ? 'mb-2 text-base' : 'mb-4 text-lg'
        )}
      >
        Club & facility
        {currentClub ? (
          <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
            {currentClub.name}
          </span>
        ) : null}
      </h2>

      <div
        className={cn(
          'grid grid-cols-2',
          compact ? 'gap-2 sm:gap-3' : 'gap-3 sm:gap-4 lg:grid-cols-4'
        )}
      >
        <DashboardStatCard
          size={statSize}
          icon={<Users aria-hidden />}
          iconBg="bg-violet-100 dark:bg-violet-900/30"
          iconColor="text-violet-600 dark:text-violet-400"
          label="Active members"
          mobileLabel="Members"
          value={statsLoading ? <StatSkeleton compact={compact} /> : overview?.activeMembers ?? 0}
        />
        <DashboardStatCard
          size={statSize}
          icon={<Clock aria-hidden />}
          iconBg="bg-teal-100 dark:bg-teal-900/30"
          iconColor="text-teal-600 dark:text-teal-400"
          label="Court hire today"
          mobileLabel="Hire today"
          value={statsLoading ? <StatSkeleton compact={compact} /> : overview?.courtHireBookingsToday ?? 0}
        />
        <DashboardStatCard
          size={statSize}
          icon={<CreditCard aria-hidden />}
          iconBg="bg-emerald-100 dark:bg-emerald-900/30"
          iconColor="text-emerald-600 dark:text-emerald-400"
          label="Active memberships"
          mobileLabel="Memberships"
          value={statsLoading ? <StatSkeleton compact={compact} /> : overview?.membershipsActive ?? 0}
        />
        <DashboardStatCard
          size={statSize}
          icon={<MapPin aria-hidden />}
          iconBg="bg-slate-100 dark:bg-slate-800/50"
          iconColor="text-slate-600 dark:text-slate-300"
          label="Courts"
          value={statsLoading ? <StatSkeleton compact={compact} /> : overview?.courtsTotal ?? 0}
        />
      </div>
    </section>
  );
}
